import axios from 'axios';
import routingConfig from '../../config/routingConfig';
import { isPublicPath } from '@root/app/routing/routeManifest';
import {
    AccountRequest,
    LoginResourceApi,
    PeopleResourceApi,
    PersonProfileDTOCapabilitiesEnum,
    UserProfileResourceApi,
    UserSelfProfileUpdateDTO
} from '@root/api';
import { translateAxiosError, TranslatedAxiosError } from '../services/migrated/axiosWrapper';
import { analyticsService } from './analytics.service';
import { log, logAttributes } from './logging.service';
import { intercomService } from './intercom.service';
import { stopStatusPolling } from './company_status.service';
import { appSettings } from '../config/settings';
import { doPost } from '../services/migrated/axiosWrapper';
import { changeLanguage } from '../../config/i18next.config';

type AuthCallback<T = unknown> = (err: TranslatedAxiosError | null, result?: T) => void;

const CACHE_KEY = 'happyForceUser';
const accessLevels = routingConfig.accessLevels;
const userRoles = routingConfig.userRoles;

export interface StoredUser {
    id?: string;
    username?: string;
    email?: string;
    name?: string;
    role?: string;
    roleBitMask?: number;
    sessionId?: string;
    language?: string;
    profileImagePath?: string;
    pureAdvisor?: boolean;
}

const cleanUser = (): StoredUser => ({
    username: '',
    email: '',
    role: userRoles.PUBLIC.title,
    roleBitMask: userRoles.PUBLIC.bitMask,
    sessionId: ''
});

const getStorageRaw = (): StoredUser => {
    try {
        const raw = localStorage.getItem(`ngStorage-${CACHE_KEY}`);
        if (!raw) {
            return cleanUser();
        }
        return JSON.parse(raw) as StoredUser;
    } catch {
        return cleanUser();
    }
};

const saveStorageUser = (user: StoredUser): void => {
    localStorage.setItem(`ngStorage-${CACHE_KEY}`, JSON.stringify(user));
};

const removeStorageUser = (): void => {
    localStorage.removeItem(`ngStorage-${CACHE_KEY}`);
};

const setRoleBitmaskFromRoleName = (user: StoredUser): void => {
    Object.keys(userRoles).forEach((roleIndex) => {
        if (userRoles[roleIndex].title === user.role) {
            user.roleBitMask = userRoles[roleIndex].bitMask;
        }
    });
};

const changeUser = (user: StoredUser, sessionId?: string): void => {
    const stored = getStorageRaw();
    Object.assign(stored, user);
    if (sessionId) {
        stored.sessionId = sessionId;
    }
    saveStorageUser(stored);
    if (user.language) {
        changeLanguage(user.language);
    }
};

const loginApi = new LoginResourceApi();
const userProfileApi = new UserProfileResourceApi();

const buildUserEventProperties = (companyUser: StoredUser, company?: { id?: string; name?: string }) => ({
    user_id: companyUser.id,
    email: companyUser.email,
    name: companyUser.name,
    user_role: companyUser.role,
    company_id: company?.id,
    company_name: company?.name
});

type LoginMethod = 'password' | 'activation_token' | 'google' | 'microsoft' | 'hp_oauth';

const inferLoginMethod = (credentials: Record<string, unknown>): LoginMethod => {
    if (credentials.activationToken) {
        return 'activation_token';
    }
    if (credentials.hpToken) {
        return 'hp_oauth';
    }
    if (credentials.googleIdToken) {
        return 'google';
    }
    if (credentials.microsoftIdToken) {
        return 'microsoft';
    }
    return 'password';
};

const buildLoginFailedProperties = (
    credentials: Record<string, unknown>,
    err: TranslatedAxiosError
) => ({
    login_method: inferLoginMethod(credentials),
    http_status: err.status ?? undefined,
    error_code: typeof err.data?.errorCode === 'string' ? err.data.errorCode : undefined,
    email: typeof credentials.email === 'string' ? credentials.email : undefined
});

export const user = (): StoredUser => getStorageRaw();

export const sessionId = (): string => getStorageRaw().sessionId || '';

export const getLanguage = (): string => getStorageRaw().language || 'en';

export const isLoggedIn = (): boolean => {
    const role = getStorageRaw().role;
    return role === userRoles.ADMIN.title
        || role === userRoles.ADVISOR.title
        || role === userRoles.RESPONSIBLE.title
        || role === userRoles.MANAGER.title;
};

export const userHasRole = (roles: string | string[]): boolean => {
    const currentRole = getStorageRaw().role;
    const checkRole = (role: string) => currentRole === role || currentRole === 'ADVISOR';
    if (Array.isArray(roles)) {
        return roles.some(checkRole);
    }
    return checkRole(roles);
};

export const isADMIN = (): boolean => getStorageRaw().role === 'ADMIN';
export const isADVISOR = (): boolean => getStorageRaw().role === 'ADVISOR';
export const isPUBLIC = (): boolean => getStorageRaw().role === 'PUBLIC';
export const isRESPONSIBLE = (): boolean => getStorageRaw().role === 'RESPONSIBLE';
export const isMANAGER = (): boolean => getStorageRaw().role === 'MANAGER';
/** Global advisor (not tied to an account); the only one allowed to manage advisors. */
export const isPureAdvisor = (): boolean => isADVISOR() && getStorageRaw().pureAdvisor === true;

export const isPublic = (pathOrState: string): boolean => isPublicPath(pathOrState);

const peopleApi = new PeopleResourceApi();

/**
 * The logged-in user's capabilities (e.g. CAN_ANSWER_CONVERSATIONS) are not part
 * of the login/profile `companyUser` (a UserDTO), so they are not in StoredUser.
 * They live on PersonProfileDTO, fetched by person id. Cache the promise per id
 * so repeated callers (menu, route guard) share a single request.
 */
let capabilitiesCache: { id: string; promise: Promise<Set<PersonProfileDTOCapabilitiesEnum>> } | null = null;

export const getMyCapabilities = (): Promise<Set<PersonProfileDTOCapabilitiesEnum>> => {
    const id = getStorageRaw().id;
    if (!id) {
        return Promise.resolve(new Set());
    }
    if (capabilitiesCache && capabilitiesCache.id === id) {
        return capabilitiesCache.promise;
    }
    const promise = peopleApi.getPersonProfile(id)
        .then((response) => new Set(response.data?.capabilities ?? []))
        .catch(() => {
            // On failure, don't cache so a later call can retry; assume no restriction.
            capabilitiesCache = null;
            return new Set<PersonProfileDTOCapabilitiesEnum>();
        });
    capabilitiesCache = { id, promise };
    return promise;
};

/**
 * True when the current user may reply to conversations. Non-managers
 * (admins/advisors) are always allowed; managers are allowed only when they hold
 * the CAN_ANSWER_CONVERSATIONS capability. Mirrors the backend semantics.
 */
export const canAnswerConversations = (): Promise<boolean> => {
    if (!isMANAGER()) {
        return Promise.resolve(true);
    }
    return getMyCapabilities().then((caps) =>
        caps.has(PersonProfileDTOCapabilitiesEnum.CanAnswerConversations)
    );
};

export const getUserImage = (): string => {
    const imagePath = getStorageRaw().profileImagePath;
    if (!imagePath) {
        const name = getStorageRaw().name;
        return `https://dummyimage.com/35x35&text=${name ? name[0] : 'HF'}`;
    }
    return imagePath;
};

export const me = (callback: AuthCallback<{ companyUser: StoredUser; sessionId: string }>): void => {
    userProfileApi.getUserProfile()
        .then((response) => {
            const data = response.data as { companyUser: StoredUser; sessionId: string };
            if (!data?.companyUser) {
                callback(translateAxiosError(new Error('Invalid session response')));
                return;
            }
            setRoleBitmaskFromRoleName(data.companyUser);
            changeUser(data.companyUser, data.sessionId);
            callback(null, data);
        })
        .catch((error) => callback(translateAxiosError(error)));
};

/** Stores the UserDTO returned by a company change (it carries the role and pureAdvisor flag for the new company). */
export const storeCompanyUser = (companyUser: StoredUser): void => {
    if (!companyUser) {
        return;
    }
    setRoleBitmaskFromRoleName(companyUser);
    changeUser(companyUser);
};

export const updateUserExternal =(loginResponse: { companyUser: StoredUser; sessionId: string }, success?: () => void): void => {
    changeUser(loginResponse.companyUser, loginResponse.sessionId);
    if (success) {
        success();
    }
};

export const login = (
    credentials: Record<string, unknown>,
    success: (data: unknown) => void,
    error: (err: TranslatedAxiosError) => void
): void => {
    loginApi.login({ data: credentials })
        .then((response) => {
            const data = response.data as { companyUser: StoredUser; sessionId: string };
            setRoleBitmaskFromRoleName(data.companyUser);
            changeUser(data.companyUser, data.sessionId);
            analyticsService.identifyUserContext({
                userId: data.companyUser.id,
                email: data.companyUser.email,
                name: data.companyUser.name,
                role: data.companyUser.role
            });
            analyticsService.track('Login Succeeded', {
                login_method: inferLoginMethod(credentials),
                ...buildUserEventProperties(data.companyUser)
            });
            success(data);
        })
        .catch((err) => {
            const translated = translateAxiosError(err);
            analyticsService.track('Login Failed', buildLoginFailedProperties(credentials, translated));

            // Example: `log.fmt` is a *tagged template*, not a function call. Each
            // interpolated value is bound as its own searchable `message.parameter.N`
            // while the messages still group under one template, so you can read
            // "Login failed via google — BAD_CREDENTIALS" and still chart failures by
            // method without parsing strings.
            //
            // Note what is absent: no email, no credentials. `beforeSendLog` would
            // scrub an obvious key, but the real rule is to not send PII in the first
            // place. `login_method` answers "is SSO broken?" without naming anyone.
            const loginMethod = inferLoginMethod(credentials);
            const errorCode = typeof translated.data?.errorCode === 'string' ? translated.data.errorCode : 'unknown';

            log.warn(log.fmt`Login failed via ${loginMethod} — ${errorCode}`, logAttributes({
                'auth.login_method': loginMethod,
                'auth.error_code': errorCode,
                'http.response.status_code': translated.status ?? undefined
            }));

            error(translated);
        });
};

export const logout = (success?: () => void, error?: AuthCallback): void => {
    stopStatusPolling();
    loginApi.logout()
        .then(() => {
            analyticsService.track('Logout Succeeded');
            analyticsService.clearUserContextCache();
            analyticsService.reset();
            intercomService.shutdown();
            removeStorageUser();
            if (success) {
                success();
            }
        })
        .catch((err) => {
            removeStorageUser();
            if (error) {
                error(translateAxiosError(err));
            }
        });
};

export const invalidate = (): void => {
    stopStatusPolling();
    changeUser(cleanUser(), '');
    removeStorageUser();
    capabilitiesCache = null;
};

export const updateUser = (
    userUpdate: UserSelfProfileUpdateDTO,
    success: (user: StoredUser) => void,
    error: (err: TranslatedAxiosError) => void
): void => {
    userProfileApi.updateUserProfile(userUpdate)
        .then((response) => {
            const data = response.data as { companyUser: StoredUser; sessionId: string };
            setRoleBitmaskFromRoleName(data.companyUser);
            changeUser(data.companyUser, data.sessionId);
            intercomService.update({
                email: data.companyUser.email,
                role: data.companyUser.role,
                name: data.companyUser.name
            });
            success(data.companyUser);
        })
        .catch((err) => error(translateAxiosError(err)));
};

export const setUserImage = (
    file: File,
    success: (user: StoredUser) => void,
    progress?: (loaded: number, total: number) => void,
    error?: () => void
): void => {
    const formData = new FormData();
    formData.append('file', file);

    axios.post(`${appSettings.APIUrl}/company/user/image`, formData, {
        headers: {
            SessionId: sessionId(),
            'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (event) => {
            if (progress && event.total) {
                progress(event.loaded, event.total);
            }
        }
    })
        .then((response) => {
            const updatedUser = response.data as StoredUser;
            changeUser(updatedUser, sessionId());
            success(updatedUser);
        })
        .catch(() => {
            if (error) {
                error();
            }
        });
};

export const sendEmployeeNewCode = (
    email: string,
    success: () => void,
    error: (err: TranslatedAxiosError) => void
): void => {
    doPost(`/employee/resend/${email}`, {}, {}, (err) => {
        if (err) {
            error(err);
            return;
        }
        success();
    });
};

export const forget = (
    payload: Pick<AccountRequest, 'email'>,
    success: () => void,
    error: (err: TranslatedAxiosError) => void
): void => {
    loginApi.forget({ email: payload.email })
        .then(() => {
            analyticsService.track('Password Reset Requested', { success: true });
            success();
        })
        .catch((err) => {
            analyticsService.track('Password Reset Requested', { success: false });
            error(translateAxiosError(err));
        });
};

export const sendAccessLink = (
    payload: Pick<AccountRequest, 'expiredToken'>,
    success: () => void,
    error: (err: TranslatedAxiosError) => void
): void => {
    loginApi.sendAccessLink({ expiredToken: payload.expiredToken })
        .then(() => {
            analyticsService.track('Access Link Requested', { success: true });
            success();
        })
        .catch((err) => {
            analyticsService.track('Access Link Requested', { success: false });
            error(translateAxiosError(err));
        });
};

export const authService = {
    user,
    sessionId,
    getLanguage,
    isLoggedIn,
    isPublic,
    userHasRole,
    isADMIN,
    isADVISOR,
    isPUBLIC,
    isRESPONSIBLE,
    isMANAGER,
    isPureAdvisor,
    storeCompanyUser,
    getMyCapabilities,
    canAnswerConversations,
    getUserImage,
    me,
    updateUserExternal,
    login,
    logout,
    invalidate,
    updateUser,
    setUserImage,
    sendEmployeeNewCode,
    forget,
    sendAccessLink,
    accessLevels,
    userRoles,
    APIUrl: appSettings.APIUrl
};
